import { useEffect, useState } from 'react';
import { formatDateForInput } from '../../../../../utils/Texts';

const EMPTY = {
  idPersona: 0,
  puesto: '',
  departamento: '',
  empresa: '',
  fechaInicio: '',
  fechaFin: '',
  activo: true,
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  fkValue = 0,
  onClose,
  onSubmit,
  inline = false,
}) {
  EMPTY.idPersona = fkValue;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    empresa: '',
    departamento: '',
    puesto: '',
    fechaInicio: '',
    fechaFin: '',
  });

  const validateField = (field, rawValue, currentForm = form) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'empresa':
        if (!value) error = 'Captura la empresa.';
        break;
      case 'departamento':
        if (!value) error = 'Captura el área.';
        break;
      case 'puesto':
        if (!value) error = 'Captura el puesto.';
        break;
      case 'fechaInicio':
        if (!value) error = 'Selecciona la fecha de inicio.';
        else if (currentForm.fechaFin && value > currentForm.fechaFin) {
          error = 'La fecha de inicio no puede ser posterior a la fecha fin.';
        }
        break;
      case 'fechaFin':
        if (!value) error = 'Selecciona la fecha de fin.';
        else if (currentForm.fechaInicio && value < currentForm.fechaInicio) {
          error = 'La fecha fin no puede ser anterior a la fecha inicio.';
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const validateAll = () => {
    const okEmp = validateField('empresa', form.empresa);
    const okDep = validateField('departamento', form.departamento);
    const okPue = validateField('puesto', form.puesto);
    const okIni = validateField('fechaInicio', form.fechaInicio);
    const okFin = validateField('fechaFin', form.fechaFin);
    return okEmp && okDep && okPue && okIni && okFin;
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const nextForm = { ...prev, [field]: value };
      
      // Cross-validation para fechas
      if (field === 'fechaInicio') {
        validateField('fechaInicio', value, nextForm);
        validateField('fechaFin', nextForm.fechaFin, nextForm);
      } else if (field === 'fechaFin') {
        validateField('fechaFin', value, nextForm);
        validateField('fechaInicio', nextForm.fechaInicio, nextForm);
      } else if (field !== 'activo') {
        validateField(field, value, nextForm);
      }
      
      return nextForm;
    });
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        idPersona: data.idPersona ?? fkValue ?? 0,
        puesto: data.puesto ?? '',
        departamento: data.departamento ?? '',
        empresa: data.empresa ?? '',
        fechaInicio: formatDateForInput(data.fechaInicio),
        fechaFin: formatDateForInput(data.fechaFin),
        activo: data.activo ?? true,
      };
      setForm(filled);
      validateField('empresa', filled.empresa);
      validateField('departamento', filled.departamento);
      validateField('puesto', filled.puesto);
      validateField('fechaInicio', filled.fechaInicio);
      validateField('fechaFin', filled.fechaFin);
    } else {
      setForm({
        ...EMPTY,
        idPersona: fkValue,
      });
      setErrors({ empresa: '', departamento: '', puesto: '', fechaInicio: '', fechaFin: '' });
    }
  }, [mode, data, fkValue]);

  const isSaveDisabled =
    !form.empresa?.trim() ||
    !form.departamento?.trim() ||
    !form.puesto?.trim() ||
    !form.fechaInicio ||
    !form.fechaFin ||
    !!errors.empresa ||
    !!errors.departamento ||
    !!errors.puesto ||
    !!errors.fechaInicio ||
    !!errors.fechaFin;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '.25rem',
  };

  if (!open) return null;

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva ocupación' : 'Editar ocupación'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empresa:</label>
        <br /><br />
        <input
          className={`infoField${errors.empresa ? ' infoField--error' : ''}`}
          value={form.empresa}
          onChange={(e) => handleChange('empresa', e.target.value)}
          maxLength={150}
        />
        {errors.empresa && <small style={errorStyle}>{errors.empresa}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Área:</label>
        <br /><br />
        <input
          className={`infoField${errors.departamento ? ' infoField--error' : ''}`}
          value={form.departamento}
          onChange={(e) => handleChange('departamento', e.target.value)}
          maxLength={120}
        />
        {errors.departamento && <small style={errorStyle}>{errors.departamento}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Puesto:</label>
        <br /><br />
        <input
          className={`infoField${errors.puesto ? ' infoField--error' : ''}`}
          value={form.puesto}
          onChange={(e) => handleChange('puesto', e.target.value)}
          maxLength={120}
        />
        {errors.puesto && <small style={errorStyle}>{errors.puesto}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Inicio:</label>
        <br /><br />
        <input
          className={`infoField${errors.fechaInicio ? ' infoField--error' : ''}`}
          type="date"
          value={form.fechaInicio}
          onChange={(e) => handleChange('fechaInicio', e.target.value)}
        />
        {errors.fechaInicio && <small style={errorStyle}>{errors.fechaInicio}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Fin:</label>
        <br /><br />
        <input
          className={`infoField${errors.fechaFin ? ' infoField--error' : ''}`}
          type="date"
          value={form.fechaFin}
          onChange={(e) => handleChange('fechaFin', e.target.value)}
        />
        {errors.fechaFin && <small style={errorStyle}>{errors.fechaFin}</small>}
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (!validateAll()) return;
          onSubmit(form, mode);
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(720px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
